//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import LoginOutlined from '@mui/icons-material/LoginOutlined';
import { BranchGroup, TourInfo, TourStep } from '@/components/Help/tourTypes';
import { ERoutesRoot } from '@/routes/RoutesEnum';

type AuthBranches = BranchGroup & {
  BasicAuth: TourStep[];
  FilePKIAuth: TourStep[];
  PKIAuth: TourStep[];
  KerberosAuth: TourStep[];
};

const loginBranches: { authentication: AuthBranches } = {
  authentication: {
    get currentBranch() {
      const selector = document.querySelector('[data-joyride-id="login-auth"]');
      const input = selector?.querySelector<HTMLInputElement>('input');
      return input?.value ?? '';
    },
    BasicAuth: [
      // Step: Username field with validation.
      {
        target: '[data-joyride-id="login-username"]',
        title: 'Username',
        content: 'Enter your username here. The Next button will be enabled ' +
                 "once you've typed something.",
        placement: 'left',
        blockTargetInteraction: false,
        autoFocus: true,
        actionHint: 'Type your username to continue',
        beforeStep: (tourActions, tourState) => {
          const textField =
            document.querySelector('[data-joyride-id="login-username"]');
          const usernameField =
            textField?.querySelector<HTMLInputElement>('input');

          if (usernameField) {
            // Setting the value of isNextDisabled can cause a rerender.  Keep
            // track of the last value so the state is only updated if the value
            // changes.
            let nextDisabled = tourState.isNextDisabled;

            const updateDisabled = (value: string): void => {
              const disableButton = !(value.trim().length > 0);
              if (disableButton !== nextDisabled) {
                // The requestAnimationFrame() prevents a loss of focus.
                requestAnimationFrame(() => {
                  tourActions.setIsNextDisabled(disableButton);
                });
                nextDisabled = disableButton;
              }
            };

            // Check initial value.
            updateDisabled(usernameField.value);

            const checkInputHandler = (e: Event) => {
              if (e.target instanceof HTMLInputElement)
                updateDisabled(e.target.value);
            };

            // Attach event listener to handle all changes after the initial
            // value.
            usernameField.addEventListener('input', checkInputHandler);

            // Return cleanup function for the listener.
            return () => {
              usernameField.removeEventListener('input', checkInputHandler);
            };
          }
        }
      },

      // Step: Password field.
      {
        target: '[data-joyride-id="login-password"]',
        title: 'Password',
        content: 'Enter your password.',
        placement: 'left',
        blockTargetInteraction: false,
        autoFocus: true
      },
    ],
    FilePKIAuth: [
      // Step: Public certificate.
      {
        target: '[data-joyride-id="login-file-certificate"]',
        title: 'Public Certificate',
        content: 'Select your public certificate file. The Next button will ' +
                 "be enabled once you've selected a file.",
        placement: 'left',
        blockTargetInteraction: false,
        actionHint: 'Select your public certificate file to continue',
        beforeStep: (tourActions) => {
          const el = document.querySelector(
            '[data-joyride-id="login-file-certificate"]');
          const fileInput = el instanceof HTMLInputElement ? el : null;

          if (fileInput?.files?.length === 0) {
            tourActions.setIsNextDisabled(true);

            const detectFileSelectHandler = (e: Event): void => {
              if (e.target instanceof HTMLInputElement &&
                  (e.target.files?.length ?? 0) > 0)
                tourActions.setIsNextDisabled(false);
            };

            // Attach event listener to handle all changes after the initial
            // value.
            fileInput.addEventListener('change', detectFileSelectHandler);

            // Return cleanup function for the listener.
            return () => {
              fileInput.removeEventListener('change', detectFileSelectHandler);
            };
          }
        }
      },
      // Step: Private key.
      {
        target: '[data-joyride-id="login-file-key"]',
        title: 'Private Key',
        content: 'Select your private key file. The Next button will be ' +
                 "enabled once you've selected a file.",
        placement: 'left',
        blockTargetInteraction: false,
        actionHint: 'Select your private key file to continue',
        beforeStep: (tourActions) => {
          const el = document.querySelector(
            '[data-joyride-id="login-file-key"]');
          const fileInput = el instanceof HTMLInputElement ? el : null;

          if (fileInput?.files?.length === 0) {
            tourActions.setIsNextDisabled(true);

            const detectFileSelectHandler = (e: Event): void => {
              if (e.target instanceof HTMLInputElement &&
                  (e.target.files?.length ?? 0) > 0)
                tourActions.setIsNextDisabled(false);
            };

            // Attach event listener to handle all changes after the initial
            // value.
            fileInput.addEventListener('change', detectFileSelectHandler);

            // Return cleanup function for the listener.
            return () => {
              fileInput.removeEventListener('change', detectFileSelectHandler);
            };
          }
        }
      },
    ],
    PKIAuth: [
      // Step: PKI Auth
      {
        target: '[data-joyride-id="login-pki"]',
        title: 'PKI Auth',
        content: 'With PKI Authentication the PKI certs stored in your ' +
                 'browser are used to log you into xGT.',
        placement: 'left'
      },
    ],
    KerberosAuth: [
      // Step: Principal field with validation.
      {
        target: '[data-joyride-id="login-principal"]',
        title: 'Kerberos Principal',
        content: 'Enter your Kerberos principal. The Next button will be ' +
                 "enabled once you've typed something.",
        placement: 'left',
        blockTargetInteraction: false,
        autoFocus: true,
        actionHint: 'Type your principal to continue',
        beforeStep: (tourActions, tourState) => {
          const textField =
            document.querySelector('[data-joyride-id="login-principal"]');
          const principalField =
            textField?.querySelector<HTMLInputElement>('input');

          if (principalField) {
            // Setting the value of isNextDisabled can cause a rerender.  Keep
            // track of the last value so the state is only updated if the value
            // changes.
            let nextDisabled = tourState.isNextDisabled;

            const updateDisabled = (value: string): void => {
              const disableButton = !(value.trim().length > 0);
              if (disableButton !== nextDisabled) {
                // The requestAnimationFrame() prevents a loss of focus.
                requestAnimationFrame(() => {
                  tourActions.setIsNextDisabled(disableButton);
                });
                nextDisabled = disableButton;
              }
            };

            // Check initial value.
            updateDisabled(principalField.value);

            const checkInputHandler = (e: Event) => {
              if (e.target instanceof HTMLInputElement)
                updateDisabled(e.target.value);
            };

            // Attach event listener to handle all changes after the initial
            // value.
            principalField.addEventListener('input', checkInputHandler);

            // Return cleanup function for the listener.
            return () => {
              principalField.removeEventListener('input', checkInputHandler);
            };
          }
        }
      },
    ]
  }
};

const loginSteps: TourStep[] = [
  // Step: Server host field.
  {
    target: '[data-joyride-id="login-host"]',
    title: 'xGT Server Host',
    content: 'This is the xGT Server Host field. In many cases, the default ' +
             'value of "xgt" should be provided. If you are connecting to a ' +
             'host running on a different machine, you may need to provide ' +
             'the IP address or hostname.',
    placement: 'bottom',
    blockTargetInteraction: false,
    autoFocus: true
  },

  // Step: Server port field.
  {
    target: '[data-joyride-id="login-port"]',
    title: 'xGT Server port',
    content: 'This is the xGT Server Port field. In most cases, the default ' +
             'value of 4637 should be used.',
    placement: 'bottom',
    blockTargetInteraction: false,
    autoFocus: true
  },

  // Step: Authentication type field.
  {
    target: '[data-joyride-id="login-auth"]',
    title: 'Authentication Method',
    content: 'Select the authentication method to use. The default ' +
             '"BasicAuth" option is suitable for most users.',
    placement: 'left',
    blockTargetInteraction: false,
    beforeStep: (tourActions, tourState, replaceStepSequence) => {
      // Setup an observer that changes the tour steps when the authentication
      // type changes.
      const selector = document.querySelector('[data-joyride-id="login-auth"]');
      const input = selector?.querySelector<HTMLInputElement>('input');

      if (input && replaceStepSequence) {
        const observer = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' &&
                mutation.attributeName === 'value') {
              const newBranchSteps = loginBranches.authentication[input.value];
              if (Array.isArray(newBranchSteps))
                replaceStepSequence('authentication', newBranchSteps);
            }
          }
        });

        observer.observe(input,
                         { attributes: true, attributeFilter: ['value'] });

        // Return cleanup function for the observer.
        return () => { observer.disconnect(); };
      }
    }
  },

  // Step: Placeholder for authentication if branch steps.
  {
    target: 'none',
    title: 'Placeholder',
    content: 'Placeholder for authentication branch steps.',
    branch: 'authentication'
  },

  // Step: Login submit button.
  {
    target: '[data-joyride-id="login-login"]',
    title: 'Login Button',
    content: 'Click here to log in after entering your credentials.',
    placement: 'left',
    blockTargetInteraction: false,
    actionHint: 'Click the Login button to complete the login process',
    beforeStep: (tourActions) => { tourActions.setIsNextDisabled(true); },
    advanceOnClick: true,
    nextPath: ERoutesRoot.Dashboard
  }
];

export const loginTourInfo: TourInfo = {
  id: 'login',
  title: 'Login Process',
  description: 'How to log in to the application',
  icon: <LoginOutlined />,
  steps: loginSteps,
  branches: loginBranches
};
